import { getScorLanguage, scorT } from 'i18n/scorLocale';
import { AuthState, AuthStatus } from 'slices/modules/auth/authState';
import { SHARESPRO_API_BASE_URL } from 'rtkQuery/sharespro/sharesproApiService';

export class ScorFileError extends Error {
  constructor(public status: number, public code?: string, message?: string) {
    super(message ?? scorT(status === 404 ? 'fileUnavailable' : 'fileLoadFailed'));
  }
}

// Keep the binary out of Redux, persistent caches and public object URLs.
export const fetchScorReportFile = async (
  url: string,
  auth: AuthState,
  signal: AbortSignal,
  onProgress?: (loaded: number) => void
): Promise<Uint8Array> => {
  let target: URL;
  try {
    target = new URL(url);
  } catch {
    throw new ScorFileError(404);
  }
  const isSigned = ['id', 'kind', 'u', 'e', 's'].every((key) => Boolean(target.searchParams.get(key)));
  if (auth.status !== AuthStatus.LoggedIn || target.username || target.password ||
    (isSigned ? target.protocol !== 'https:' : target.origin !== new URL(SHARESPRO_API_BASE_URL).origin)) {
    throw new ScorFileError(404);
  }
  // Signed URLs from files[].url authorize themselves and select the file version.
  // Sending SID would override that version; only legacy API URLs need headers.
  const response = await fetch(url, {
    signal,
    cache: 'no-store',
    redirect: 'error',
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
    headers: isSigned ? undefined : {
      [process.env.REACT_APP_SHARESPRO_API_CREDENTIALS_HEADER_KEY ?? 'SID']: auth.sid,
      'APP-LANGUAGE': getScorLanguage() === 'en' ? '2' : '1',
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ScorFileError(response.status, body.error_code, body.error_message);
  }
  if (!response.headers.get('content-type')?.toLowerCase().includes('application/pdf')) {
    throw new ScorFileError(response.status);
  }

  onProgress?.(0);
  if (!response.body) {
    const bytes = new Uint8Array(await response.arrayBuffer());
    onProgress?.(bytes.byteLength);

    return bytes;
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value.byteLength === 0) continue;
      chunks.push(value);
      loaded += value.byteLength;
      onProgress?.(loaded);
    }
  } finally {
    reader.releaseLock();
  }

  const bytes = new Uint8Array(loaded);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }

  return bytes;
};
